import type { RunView, TenantRow } from '../schemas/bulk-migration';

/**
 * One tenant's run, after a launch or a refresh, in words. The runner reports
 * how the start went (`accepted`, `blocked`, `already-running`) and, once a
 * refresh has read it, the run's status and totals.
 */

const START_COPY: Readonly<Record<RunView['start'], string>> = {
    accepted: 'Started',
    blocked: 'Blocked before starting',
    'already-running': 'Already running',
};

export function runStatusText(run: RunView | undefined): string {
    if (run === undefined) return '—';
    if (run.start !== 'accepted') return START_COPY[run.start];
    return run.status ?? START_COPY.accepted;
}

/** `Migrated 12 · Error 1`, largest first; empty when the run has no totals yet. */
export function runTotalsText(run: RunView | undefined): string {
    const totals = run?.totals;
    if (totals === undefined) return '';
    return Object.entries(totals)
        .filter(([, count]) => count > 0)
        .sort(([, a], [, b]) => b - a)
        .map(([status, count]) => `${status} ${count.toLocaleString('en-US')}`)
        .join(' · ');
}

/** Warnings and failed records among the records the row carries (at most 100 of them). */
export function recordIssues(row: Pick<TenantRow, 'records'>): {
    readonly warnings: number;
    readonly failures: number;
} {
    let warnings = 0;
    let failures = 0;
    for (const record of row.records) {
        warnings += record.tracking?.warnings.length ?? 0;
        const failure = record.tracking?.failure;
        if (failure !== undefined && failure !== null) failures += 1;
    }
    return { warnings, failures };
}

/** "2 warnings · 1 failed", or `null` when the records carry neither. */
export function issuesText(issues: {
    readonly warnings: number;
    readonly failures: number;
}): string | null {
    if (issues.warnings + issues.failures === 0) return null;
    const parts: string[] = [];
    if (issues.warnings > 0) {
        parts.push(`${String(issues.warnings)} ${issues.warnings === 1 ? 'warning' : 'warnings'}`);
    }
    if (issues.failures > 0) parts.push(`${String(issues.failures)} failed`);
    return parts.join(' · ');
}
